// Shared API contract between @harness/server and @harness/web.
// Everything the browser sees goes through these shapes; secrets never appear here.

export type ServerStatus = 'online' | 'offline' | 'unreachable';
export type ConnectionMode = 'runner' | 'ssh' | 'local' | 'daytona';
export type ToolKey = 'read' | 'write' | 'shell' | 'git' | 'web' | 'http';
export type Permission = 'allow' | 'ask' | 'deny';
export type AgentRole = 'Planner' | 'Coder' | 'Reviewer' | 'Researcher' | 'Wireframer' | 'Analyst' | 'Custom';
/** Workspace mode of a conversation: build features, draft Jira tickets, or ask questions about the code. */
export type ConversationMode = 'feature' | 'ticket' | 'wireframe' | 'analysis';
export type AgentRuntime = 'native' | 'pi' | 'opencode';
export const CONVERSATION_MODES: ConversationMode[] = ['ticket', 'wireframe', 'feature', 'analysis'];
/** The agent role that answers in each non-feature mode (always read-only there). */
export const MODE_ROLE: Record<Exclude<ConversationMode, 'feature'>, AgentRole> = { ticket: 'Researcher', wireframe: 'Wireframer', analysis: 'Analyst' };
/** Roles that belong to a mode tab (kept out of Build Features). */
export const MODE_ROLES: AgentRole[] = ['Researcher', 'Wireframer', 'Analyst'];
/** A person's role in an organisation or project: viewers read, editors work, owners administer. */
export type OrgRole = 'viewer' | 'editor' | 'owner';
export const ORG_ROLES: OrgRole[] = ['viewer', 'editor', 'owner'];
export const roleAtLeast = (have: OrgRole | null | undefined, need: OrgRole) => !!have && ORG_ROLES.indexOf(have) >= ORG_ROLES.indexOf(need);
export type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';
export type Look = 'studio' | 'modernist' | 'midnight' | 'editorial';

/** Tool calls an agent may make in one turn: the default for new agents, and the most a user can set. */
export const TOOL_CALLS_DEFAULT = 250;
export const TOOL_CALLS_MAX = 1000;

export const TOOL_KEYS: ToolKey[] = ['read', 'write', 'shell', 'git', 'web', 'http'];

export const TOOL_DEFS: { key: ToolKey; label: string; desc: string }[] = [
  { key: 'read', label: 'File read', desc: 'List, read and search, confined to the working directory' },
  { key: 'write', label: 'File write', desc: 'Write and diff-based patch · checkpoint before each writing turn' },
  { key: 'shell', label: 'Shell', desc: 'Run commands · 120 s timeout · streamed output' },
  { key: 'git', label: 'Git', desc: 'Status, diff, commit, branch · push is always gated' },
  { key: 'web', label: 'Web search', desc: 'Results are marked untrusted' },
  { key: 'http', label: 'HTTP fetch', desc: 'Fetch URLs · responses are marked untrusted' },
];

export type ToolSet = Record<ToolKey, { on: boolean; perm: Permission }>;
const toolSet = (on: ToolKey[], perms: Partial<Record<ToolKey, Permission>>, fallback: Permission): ToolSet =>
  Object.fromEntries(TOOL_KEYS.map((k) => [k, { on: on.includes(k), perm: perms[k] ?? fallback }])) as ToolSet;

/** Tools each built-in role starts with. Custom gets the spec default: read-only file + search. */
export const ROLE_TOOLS: Record<AgentRole, ToolSet> = {
  Planner: toolSet(['read', 'git', 'web'], { read: 'allow', git: 'allow', web: 'ask' }, 'deny'),
  Researcher: toolSet(['read', 'git', 'web'], { read: 'allow', git: 'allow', web: 'allow' }, 'deny'),
  Analyst: toolSet(['read', 'git', 'web'], { read: 'allow', git: 'allow', web: 'allow' }, 'deny'),
  Wireframer: toolSet(['read', 'git'], { read: 'allow', git: 'allow' }, 'deny'),
  Coder: toolSet(['read', 'write', 'shell', 'git', 'web'], { read: 'allow', web: 'allow', http: 'deny' }, 'ask'),
  Reviewer: toolSet(['read', 'git'], { read: 'allow', git: 'allow' }, 'deny'),
  Custom: toolSet(['read'], { read: 'allow' }, 'ask'),
};

export const sameTools = (a: ToolSet, b: ToolSet) => TOOL_KEYS.every((k) => a[k]?.on === b[k]?.on && a[k]?.perm === b[k]?.perm);

// ── account ────────────────────────────────────────────────────────────────
export interface UserDTO {
  id: string;
  email: string;
  name: string;
  initials: string;
  /** Role in the user's organisation (null if not in one yet): 'owner' = admin of the harness. */
  role: OrgRole | null;
  isAdmin: boolean;
  organisation: { id: string; name: string } | null;
  mfaEnabled: boolean;
  passkeyCount: number;
  look: Look | null;
  theme: 'system' | 'light' | 'dark';
}

export interface SessionState {
  /** 'anonymous' → show login; 'mfa' → show 2FA step; 'ok' → signed in. */
  state: 'anonymous' | 'mfa' | 'ok';
  needsSetup: boolean;
  user: UserDTO | null;
  email?: string;
  oauth: { github: boolean; google: boolean; oidc: boolean };
  /** false → email/password sign-in is disabled (e.g. PASSWORD_LOGIN=false); only OAuth/OIDC buttons show. */
  passwordLogin: boolean;
}

export interface SessionDTO {
  id: string;
  current: boolean;
  userAgent: string;
  ip: string;
  createdAt: string;
  lastSeenAt: string;
}

// ── providers & models ────────────────────────────────────────────────────
export type Adapter = 'anthropic' | 'openai-compatible' | 'openai' | 'google' | 'bedrock' | 'azure-openai';

export const ADAPTER_LABELS: Record<Adapter, string> = {
  anthropic: 'Anthropic',
  'openai-compatible': 'OpenAI-compatible',
  openai: 'OpenAI',
  google: 'Google',
  bedrock: 'AWS Bedrock',
  'azure-openai': 'Azure OpenAI',
};

export interface ModelCapabilities {
  tools: 'native' | 'prompt' | 'none';
  vision: boolean;
  streaming: boolean;
  /** adaptive = effort-controlled thinking; budget = token budget; none = no reasoning control. */
  reasoning: 'adaptive' | 'budget' | 'none';
  temperature: boolean;
  kind: 'chat' | 'embedding';
}

export interface ModelDTO {
  id: string;
  providerId: string;
  providerName: string;
  adapter: Adapter;
  selfHosted: boolean;
  modelName: string;
  contextWindow: number;
  maxOutput: number;
  capabilities: ModelCapabilities;
  priceIn: number | null;
  priceOut: number | null;
  /** $/M for input tokens read from the provider's prompt cache; null = same as priceIn. */
  priceCachedIn: number | null;
}

export interface ProviderDTO {
  id: string;
  name: string;
  adapter: Adapter;
  baseUrl: string;
  keyHint: string | null;
  headers: Record<string, string>;
  selfHosted: boolean;
  discovery: 'auto' | 'manual';
  models: ModelDTO[];
  lastTest: { ok: boolean; latencyMs: number; message: string; at: string } | null;
}

// ── projects ──────────────────────────────────────────────────────────────
export interface ServerConnectionDTO {
  id: string;
  mode: ConnectionMode;
  host: string;
  user: string;
  port: number;
  workingDir: string;
  status: ServerStatus;
  lastSeenAt: string | null;
  lastCheckAt: string | null;
  runnerVersion: string | null;
  publicKey: string | null;
  capabilities: { fs: boolean; shell: boolean; git: boolean; port: boolean; docker: boolean };
  sandbox: string;
}

export interface ProjectDTO {
  id: string;
  slug: string;
  name: string;
  description: string;
  tags: string[];
  color: string | null;
  /** Per person: pinned for you. */
  pinned: boolean;
  archived: boolean;
  createdAt: string;
  lastActiveAt: string;
  server: ServerConnectionDTO | null;
  defaultModel: string | null;
  runningTurns: number;
  pendingGates: number;
  /** Per person: the conversation you last had open. */
  lastConversationId: string | null;
  /** Per person: your role on this project. */
  role: OrgRole;
  memoryEnabled: boolean;
  memoryTopK: number;
  branchTemplate: string;
  agentRuntime: AgentRuntime;
}

/** Fields of ProjectDTO that differ per person. Project-wide events don't carry them; keep your own. */
export const PER_USER_PROJECT_FIELDS = ['pinned', 'lastConversationId', 'role'] as const;

// ── agents ────────────────────────────────────────────────────────────────
export interface AgentParams {
  temperature: number | null;
  maxOutputTokens: number | null;
  effort: Effort | null;
  thinkingBudget: number | null;
  stopSequences: string[];
}

/** An agent in the admin-managed library: people add it to projects out of the box. */
export interface AgentTemplateDTO {
  id: string;
  name: string;
  description: string;
  role: AgentRole;
  systemPrompt: string;
  /** Used when the person has a model with this name; otherwise the model they pick. */
  preferredModel: string | null;
  params: Partial<AgentParams>;
  tools: ToolSet;
  contextFiles: string[];
  limits: { maxToolCalls: number; maxSpendUsd: number | null };
  includeByDefault: boolean;
  isDefaultAgent: boolean;
  enabled: boolean;
  sort: number;
  updatedAt: string;
}

export interface AgentConfigDTO {
  id: string;
  projectId: string;
  name: string;
  handle: string;
  role: AgentRole;
  isDefault: boolean;
  version: number;
  systemPrompt: string;
  modelId: string | null;
  fallbackModelId: string | null;
  params: AgentParams;
  tools: Record<ToolKey, { on: boolean; perm: Permission }>;
  mcpServers: { name: string; url: string }[];
  workingDir: string;
  contextFiles: string[];
  limits: { maxToolCalls: number; maxSpendUsd: number | null };
}

export interface AgentVersionDTO {
  version: number;
  note: string;
  createdAt: string;
  config: Omit<AgentConfigDTO, 'version'>;
}

// ── conversations ─────────────────────────────────────────────────────────
export type TurnStatus = 'idle' | 'running' | 'awaiting_approval' | 'error';

export interface ConversationDTO {
  id: string;
  projectId: string;
  title: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
  status: TurnStatus;
  featureRunId: string | null;
  badge: 'Gate' | 'Done' | null;
  tokensUsed: number;
  forkedFrom: string | null;
  mode: ConversationMode;
  /** Wireframes mode: the Jira ticket it's for, if any. */
  jiraKey: string | null;
  /** Ticket mode: where the spec is (draft → approved → created in Jira). */
  ticket: { status: TicketSpecStatus; jiraKey: string | null } | null;
}

// ── ticket specs (Tickets mode) ───────────────────────────────────────────
/** A high-level ticket spec the Researcher drafts from product requirements. */
export interface TicketSpec {
  title: string;
  summary: string;
  issueType?: string;
  background?: string;
  requirements: string[];
  acceptanceCriteria: string[];
  outOfScope?: string[];
  /** Where in the code this lands, from reading it (high level). */
  technicalNotes?: string;
  risks?: string[];
  suggestions?: string[];
  openQuestions?: string[];
  labels?: string[];
}

export type TicketSpecStatus = 'draft' | 'approved' | 'created';

/** A saved wireframe version (Wireframes mode). The HTML is fetched separately. */
export interface WireframeDTO {
  version: number;
  title: string;
  jiraKey: string | null;
  createdAt: string;
}

export interface TicketSpecDTO {
  conversationId: string;
  spec: TicketSpec;
  version: number;
  status: TicketSpecStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  jiraKey: string | null;
  jiraUrl: string | null;
  updatedAt: string;
}

export type Part =
  | { type: 'text'; text: string }
  | { type: 'thinking'; text: string }
  | { type: 'tool_call'; toolCallId: string };

export type NoteIcon = 'bot' | 'swap' | 'bookmark' | 'check' | 'github' | 'jira' | 'alert' | 'info';

export type EventPayload =
  | { kind: 'note'; icon: NoteIcon; text: string; spinning?: boolean }
  | { kind: 'gate'; gateId: string }
  | { kind: 'plan'; steps: { n: number; text: string; repo: string }[] }
  | { kind: 'error'; text: string; fix?: string };

export interface MessageDTO {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'event';
  /** Who wrote it (user messages). The UI shows "You" when it's the signed-in person. */
  author: { id: string; name: string } | null;
  agentId: string | null;
  agentName: string | null;
  model: string | null;
  parts: Part[];
  toolCalls: ToolCallDTO[];
  event: EventPayload | null;
  tokensIn: number;
  tokensOut: number;
  /** Part of tokensIn served from the provider's prompt cache. */
  cachedTokensIn: number;
  costUsd: number;
  createdAt: string;
  streaming: boolean;
  parentId: string | null;
}

export type ToolCallStatus = 'pending' | 'awaiting_approval' | 'running' | 'done' | 'error' | 'denied';

export interface ToolCallDTO {
  id: string;
  messageId: string;
  tool: string;
  toolKey: ToolKey | 'memory' | 'handoff' | 'history';
  input: Record<string, unknown>;
  display: string;
  output: string;
  status: ToolCallStatus;
  approvedBy: string | null;
  durationMs: number | null;
  meta: string;
  reason: string | null;
  checkpointId: string | null;
}

export interface GateDTO {
  id: string;
  featureRunId: string | null;
  kind: 'transition' | 'plan' | 'push' | 'ticket' | 'error';
  kicker: string;
  title: string;
  description: string;
  status: 'pending' | 'approved' | 'skipped' | 'rejected' | 'invalidated';
  outcome: string | null;
  primaryLabel: string;
  secondaryLabel: string | null;
}

// Server-sent events on /api/events (per user) — the browser keeps one stream open.
export interface ProjectEnvironmentDTO {
  projectId: string;
  provisioningMode: 'dedicated' | 'shared';
  appSchema: string | null;
  state: 'requested' | 'creating_supabase' | 'awaiting_supabase' | 'configuring_db' | 'creating_secret' | 'creating_sandbox' | 'bootstrapping' | 'ready' | 'paused' | 'resuming' | 'deleting' | 'deleted' | 'failed';
  step: string;
  errorCode: string | null;
  errorDetail: string | null;
  attempts: number;
  supabaseRef: string | null;
  supabaseRegion: string | null;
  supabaseApiUrl: string | null;
  daytonaSandboxId: string | null;
  previewPort: number;
  createdAt: string;
  readyAt: string | null;
  updatedAt: string;
}

export interface ProjectEnvironmentEventDTO {
  at: string;
  fromState: string | null;
  toState: string;
  step: string;
  provider: string | null;
  outcome: string;
  detail: string | null;
}

export interface ProjectEnvironmentResponse {
  environment: ProjectEnvironmentDTO | null;
  events: ProjectEnvironmentEventDTO[];
}

export type StreamEvent =
  | { type: 'hello'; at: string }
  | { type: 'ping'; at: string }
  | { type: 'message.created'; projectId: string; message: MessageDTO }
  | { type: 'message.delta'; projectId: string; conversationId: string; messageId: string; partIndex: number; kind: 'text' | 'thinking'; text: string }
  | { type: 'message.updated'; projectId: string; message: MessageDTO }
  | { type: 'message.deleted'; projectId: string; conversationId: string; messageId: string }
  | { type: 'toolcall.updated'; projectId: string; conversationId: string; toolCall: ToolCallDTO }
  | { type: 'conversation.updated'; projectId: string; conversation: ConversationDTO }
  | { type: 'gate.updated'; projectId: string; conversationId: string; gate: GateDTO }
  | { type: 'feature.updated'; projectId: string; run: FeatureRunDTO }
  | { type: 'project.updated'; project: ProjectDTO }
  | { type: 'environment.updated'; projectId: string; environment: ProjectEnvironmentDTO }
  | { type: 'memory.updated'; projectId: string }
  | { type: 'agent.updated'; projectId: string; agent: AgentConfigDTO }
  | { type: 'toast'; text: string }
  | { type: 'jira.updated'; projectId: string }
  | { type: 'ticketspec.updated'; projectId: string; conversationId: string; spec: TicketSpecDTO }
  | { type: 'wireframe.updated'; projectId: string; conversationId: string; wireframe: WireframeDTO }
  | { type: 'repos.updated'; projectId: string; repos: RepoLinkDTO[] };

// ── memory ────────────────────────────────────────────────────────────────
export interface MemoryFactDTO {
  id: string;
  text: string;
  source: 'user' | 'agent' | 'auto';
  enabled: boolean;
  createdAt: string;
}

export interface MemoryProposalDTO {
  id: string;
  text: string;
  conversationId: string | null;
  conversationTitle: string | null;
}

// ── audit & checkpoints ───────────────────────────────────────────────────
export interface AuditEventDTO {
  id: string;
  actor: string;
  action: string;
  detail: string;
  createdAt: string;
}

export interface CheckpointDTO {
  id: string;
  label: string;
  ref: string;
  files: number;
  restoredAt: string | null;
  createdAt: string;
}

// ── GitHub & Jira ─────────────────────────────────────────────────────────
export interface RepoLinkDTO {
  id: string;
  owner: string;
  repo: string;
  baseBranch: string;
  localPath: string;
  roleLabel: string;
  lastSyncAt: string | null;
  syncStatus: 'idle' | 'syncing' | 'error';
  syncError: string | null;
}

export interface JiraLinkDTO {
  siteUrl: string;
  projectKey: string;
  boardId: string;
  jqlFilter: string;
  /** oauth: uses people's own Atlassian connections; cloud/datacenter: a token stored for the project. */
  authKind: 'cloud' | 'datacenter' | 'oauth';
  cloudId: string | null;
  email: string | null;
  hasToken: boolean;
  statusMapping: { inProgress: string; inReview: string };
}

export interface JiraIssueDTO {
  key: string;
  summary: string;
  status: string;
  priority: string;
  assignee: string | null;
  labels: string[];
  components: string[];
  url: string;
}

export interface IntegrationsDTO {
  /** via 'account': someone's connected GitHub (yours, else the creator's); 'token': a token pasted for the project. */
  github: { connected: boolean; tokenHint: string | null; login: string | null; via: 'account' | 'token' | null };
  repos: RepoLinkDTO[];
  jira: JiraLinkDTO | null;
  /** Where to point GitHub/Jira webhooks, with the signing secrets to configure there. */
  webhooks?: { githubUrl: string; githubSecret: string | null; jiraUrl: string | null };
}

/** Your own GitHub / Jira sign-ins (Settings › Connections). */
export interface ConnectionsDTO {
  github: { available: boolean; connected: boolean; account: string | null; installUrl: string | null };
  atlassian: { available: boolean; connected: boolean; account: string | null; sites: { id: string; url: string; name: string }[] };
}

/** Admin › Integrations: the registered apps (secrets never returned). */
export interface AdminIntegrationsDTO {
  github: { configured: boolean; clientId: string | null; appSlug: string | null; callbackUrl: string; connectedPeople: number };
  atlassian: { configured: boolean; clientId: string | null; callbackUrl: string; connectedPeople: number };
}

export interface GithubRepoOptionDTO {
  owner: string;
  repo: string;
  fullName: string;
  defaultBranch: string;
  private: boolean;
}

export interface JiraIssueDetailDTO extends JiraIssueDTO {
  description: string;
  acceptanceCriteria: string[];
  comments: { author: string; body: string; created: string }[];
  subtasks: { key: string; summary: string; status: string }[];
  links: { key: string; summary: string; type: string }[];
}

export interface RepoProposalDTO {
  repoIds: string[];
  reason: string;
}

// ── feature runs ──────────────────────────────────────────────────────────
export type FeatureState =
  | 'awaiting_transition'
  | 'planning'
  | 'awaiting_plan'
  | 'coding'
  | 'awaiting_push'
  | 'pushing'
  | 'awaiting_ticket'
  | 'done'
  | 'failed'
  | 'cancelled';

export const FEATURE_STEPS = [
  'Pick task',
  'Move to In progress',
  'Plan',
  'Code & test',
  'Push preview',
  'Push & open PR',
  'Ticket update',
  'Done',
] as const;
export const FEATURE_GATE_STEPS = [2, 4, 6];

export interface FeatureRepoState {
  repoId: string;
  name: string;
  role: string;
  base: string;
  branch: string;
  status: string;
  prUrl: string | null;
  prNumber: number | null;
  commits: number;
  files: number;
  additions: number;
  deletions: number;
}

export interface FeatureRunDTO {
  id: string;
  projectId: string;
  conversationId: string;
  issueKey: string;
  issueSummary: string;
  state: FeatureState;
  step: number;
  repos: FeatureRepoState[];
  plan: { n: number; text: string; repo: string }[];
  error: { text: string; fix: string } | null;
  approvals: { kind: string; decidedAt: string; decision: string; previewHash: string; decidedBy: string }[];
  createdAt: string;
}

export interface DiffLine {
  t: ' ' | '+' | '-' | '@';
  n: number | null;
  s: string;
}

export interface PushPreviewDTO {
  previewHash: string;
  repos: {
    repoId: string;
    name: string;
    branch: string;
    base: string;
    fetchedAt: string | null;
    commits: { sha: string | null; message: string }[];
    checks: { label: string; ok: boolean; value: string }[];
    files: { path: string; status: 'A' | 'M' | 'D' | 'R'; additions: number; deletions: number; diff: DiffLine[] }[];
    prTitle: string;
    prBody: string;
  }[];
}

export interface TicketPreviewDTO {
  previewHash: string;
  kind: 'transition' | 'ticket';
  issueKey: string;
  fromStatus: string;
  toStatus: string;
  fieldChanges: string;
  comment: string | null;
  author: string;
}

// ── usage ─────────────────────────────────────────────────────────────────
export interface UsageRow {
  key: string;
  label: string;
  tokensIn: number;
  tokensOut: number;
  cachedTokensIn: number;
  costUsd: number;
  turns: number;
  /** Tokens from hosted models with no price set, so the cost above leaves them out. */
  unpricedTokens: number;
}

export interface UsageDTO {
  byProject: UsageRow[];
  byProvider: UsageRow[];
  byAgent: UsageRow[];
  byConversation: UsageRow[];
  /** Who started the turns. */
  byPerson: UsageRow[];
  total: UsageRow;
}

// ── admin ─────────────────────────────────────────────────────────────────
/** A person, as the admin section sees them. Admins never see anyone's projects, only counts and spend. */
export interface AdminUserDTO {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  /** 'authentik' = arrived through single sign-on; 'manual' = added by an admin. */
  source: 'manual' | 'authentik';
  disabled: boolean;
  hasPassword: boolean;
  hasSso: boolean;
  mfaEnabled: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  providerCount: number;
  projectCount: number;
  /** Last 30 days, across all their projects. */
  usage30d: { costUsd: number; tokens: number; unpricedTokens: number };
  you: boolean;
}

export interface AdminUsageRow {
  userId: string;
  name: string;
  email: string;
  projects: number;
  turns: number;
  tokensIn: number;
  cachedTokensIn: number;
  tokensOut: number;
  costUsd: number;
  unpricedTokens: number;
}

export interface OrganisationDTO {
  id: string;
  name: string;
  members: number;
  admins: number;
}

/** Someone with access to a project: its creator or a person they invited. */
export interface ProjectMemberDTO {
  userId: string;
  name: string;
  email: string;
  role: OrgRole;
  creator: boolean;
  you: boolean;
}

/** A colleague you can invite to a project. */
export interface PersonDTO {
  id: string;
  name: string;
  email: string;
}

// ── helpers ───────────────────────────────────────────────────────────────
export function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'project'
  );
}

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1000) return `${+(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return String(n);
}

export function fuzzyMatch(q: string, s: string): boolean {
  q = q.toLowerCase();
  s = s.toLowerCase();
  if (!q) return true;
  if (s.includes(q)) return true;
  let i = 0;
  for (const c of s) {
    if (c === q[i]) i++;
    if (i === q.length) return true;
  }
  return false;
}
